/**
 * /showcase — public gallery of the current user's top clips.
 *
 * Data honesty rule (per the sprint brief):
 *   - Anonymous visitors see a premium empty state + a "Commence gratuitement" CTA.
 *   - Logged-in users see their own clips ranked by viral_score (real data).
 *   - No fake clips, no seed data.
 *
 * No new backend endpoint — we reuse `/api/videos` + `/api/videos/:id/clips`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Clock, Play, ArrowRight, Film } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';

const CATEGORY_LABEL_FR = {
  podcast: 'Podcast', gaming: 'Gaming', business: 'Business', finance: 'Finance',
  education: 'Éducation', fitness: 'Fitness', lifestyle: 'Lifestyle', vlog: 'Vlog',
  interview: 'Interview', sport: 'Sport', general: 'Général',
};

const ClipCard = ({ clip, index }) => {
  const dur = Math.round(clip.duration || 0);
  const category = CATEGORY_LABEL_FR[clip.category] || clip.category || 'Général';
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ delay: (index % 12) * 0.03 }}
      className="group relative rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] hover:border-[#D4FF00]/40 transition"
      data-testid={`showcase-clip-${clip.id}`}
    >
      <Link to={`/clips/${clip.id}`} className="block">
        <div
          className="relative aspect-[9/16] w-full flex items-center justify-center overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${clip.thumbnail_color || '#1a1a1a'}, #050505)` }}
        >
          <Film className="w-8 h-8 text-white/40 group-hover:text-white/60 transition" />
          <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-md bg-[#D4FF00] text-black text-[10px] font-black uppercase tracking-wider">
            <Sparkles className="w-3 h-3" /> {Math.round(clip.viral_score || 0)}
          </div>
          <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-md bg-black/70 text-white text-[10px] font-mono">
            <Clock className="w-3 h-3" /> {dur}s
          </div>
          <div className="absolute inset-x-0 bottom-0 h-1 bg-white/10">
            <div className="h-full bg-[#D4FF00]" style={{ width: `${Math.min(100, clip.viral_score || 0)}%` }} />
          </div>
        </div>
        <div className="p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">{category}</span>
          </div>
          <h3 className="text-sm font-semibold truncate">{clip.title || 'Clip sans titre'}</h3>
        </div>
      </Link>
    </motion.div>
  );
};

const EmptyPremium = ({ lang, isAuthed }) => (
  <div
    className="max-w-2xl mx-auto text-center py-20 px-6 rounded-2xl border border-white/10 bg-white/[0.02]"
    data-testid="showcase-empty"
  >
    <div className="w-14 h-14 mx-auto rounded-2xl bg-[#D4FF00]/10 border border-[#D4FF00]/25 flex items-center justify-center mb-6">
      <Sparkles className="w-6 h-6 text-[#D4FF00]" />
    </div>
    <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight mb-3">
      {isAuthed
        ? (lang === 'fr' ? 'Aucun clip à afficher pour l\'instant.' : 'No clips to showcase yet.')
        : (lang === 'fr' ? 'La vitrine s\'ouvre après ton premier clip.' : 'The showcase opens after your first clip.')}
    </h2>
    <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-8">
      {lang === 'fr'
        ? 'Upload une vidéo longue — l\'IA identifiera automatiquement les meilleurs moments. Aucun clip factice ici : seulement du vrai contenu de la communauté.'
        : "Upload a long-form video — the AI will spot the best moments automatically. No fake clips here — only real community content."}
    </p>
    <Link to={isAuthed ? '/upload' : '/register'}>
      <Button className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold h-11 px-6 rounded-xl" data-testid="showcase-cta">
        {isAuthed
          ? (lang === 'fr' ? 'Uploader ma première vidéo' : 'Upload my first video')
          : (lang === 'fr' ? 'Commence gratuitement' : 'Start free')}
        <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </Link>
  </div>
);

const SkeletonGrid = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="rounded-2xl bg-white/[0.02] border border-white/5 overflow-hidden">
        <div className="aspect-[9/16] w-full bg-white/[0.03] animate-pulse" />
        <div className="p-3 space-y-2">
          <div className="h-2 w-16 bg-white/10 rounded animate-pulse" />
          <div className="h-3 w-24 bg-white/10 rounded animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

const Showcase = () => {
  const { lang } = useT();
  const { user } = useAuth();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(!!user);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        setLoading(true);
        const { data: videos } = await api.get('/videos');
        const withClips = (videos || []).filter((v) => (v.clips_count || 0) > 0).slice(0, 6);
        const all = [];
        for (const v of withClips) {
          try {
            const { data } = await api.get(`/videos/${v.id}/clips`);
            all.push(...(data || []));
          } catch (_) { /* skip */ }
        }
        // Sort by viral_score DESC, cap to 12.
        all.sort((a, b) => (b.viral_score || 0) - (a.viral_score || 0));
        setClips(all.slice(0, 12));
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const isAuthed = !!user;
  const showEmpty = !loading && clips.length === 0;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="showcase-page">
      <div className="text-center mb-14 max-w-3xl mx-auto">
        <span className="text-xs uppercase tracking-[0.25em] text-[#D4FF00] font-mono">
          {lang === 'fr' ? '// Vitrine' : '// Showcase'}
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black mt-4 tracking-tighter leading-[1.05]">
          {lang === 'fr' ? 'Les clips les mieux notés' : 'Top-scored clips'}
        </h1>
        <p className="text-zinc-400 mt-5 text-base sm:text-lg leading-relaxed">
          {lang === 'fr'
            ? 'Chaque clip affiche son vrai score IA, sa durée, sa catégorie. Rien n\'est simulé.'
            : "Every clip shows its real AI score, duration and category. Nothing is simulated."}
        </p>
      </div>

      {loading && <SkeletonGrid />}
      {showEmpty && <EmptyPremium lang={lang} isAuthed={isAuthed} />}
      {!loading && clips.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="showcase-grid">
          {clips.map((c, i) => <ClipCard key={c.id} clip={c} index={i} />)}
        </div>
      )}

      {/* Bottom CTA — always visible */}
      <div className="mt-20 text-center">
        <Link to={isAuthed ? '/upload' : '/register'}>
          <Button className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold h-12 px-8 text-base rounded-xl" data-testid="showcase-footer-cta">
            {isAuthed
              ? (lang === 'fr' ? 'Uploader une nouvelle vidéo' : 'Upload a new video')
              : (lang === 'fr' ? 'Commence gratuitement' : 'Start free')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default Showcase;
